import React, { useState, useEffect } from 'react';
import { FiFileText, FiEdit, FiTrash2, FiPlus } from 'react-icons/fi';
import DashboardLayout from '../../components/DashboardLayout';
import API from '../../utils/api';

const FieldNotes = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    bookingId: '',
    note: '',
    partsNeeded: '',
    timeAdjustment: '',
  });
  const [jobs, setJobs] = useState([]);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const jobsResponse = await API.get('/api/mechanic/jobs/active');
        setJobs(jobsResponse.data);
        
        const notesResponse = await API.get('/api/mechanic/field-notes');
        setNotes(notesResponse.data);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingId) {
        await API.put(`/api/mechanic/field-note/${editingId}`, {
          note: formData.note,
          parts_needed: formData.partsNeeded,
          time_adjustment: formData.timeAdjustment
        });
        
        const updatedNotesResponse = await API.get('/api/mechanic/field-notes');
        setNotes(updatedNotesResponse.data);
        
        setEditingId(null);
      } else {
        await API.post('/api/mechanic/field-note', {
          booking_id: formData.bookingId,
          note: formData.note,
          parts_needed: formData.partsNeeded,
          time_adjustment: formData.timeAdjustment
        });
        
        const updatedNotesResponse = await API.get('/api/mechanic/field-notes');
        setNotes(updatedNotesResponse.data);
      }
      
      // Reset form
      setFormData({
        bookingId: '',
        note: '',
        partsNeeded: '',
        timeAdjustment: '',
      });
      
      setShowForm(false);
    } catch (error) {
      console.error('Error saving field note:', error);
    }
  };

  const handleEdit = (note) => {
    setFormData({
      bookingId: note.booking_id,
      note: note.note,
      partsNeeded: note.parts_needed || '',
      timeAdjustment: note.time_adjustment || '',
    });
    setEditingId(note.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus catatan ini?')) {
      try {
        await API.delete(`/api/mechanic/field-note/${id}`);
        setNotes(notes.filter(note => note.id !== id));
      } catch (error) {
        console.error('Error deleting field note:', error);
      }
    }
  };

  const getSeverityClass = (note) => {
    if (note.parts_needed && note.time_adjustment) {
      return 'bg-red-100 text-red-800'; // High severity
    } else if (note.parts_needed || note.time_adjustment) {
      return 'bg-yellow-100 text-yellow-800'; // Medium severity
    } else {
      return 'bg-green-100 text-green-800'; // Low severity
    }
  };

  const getIssueType = (note) => {
    if (note.parts_needed && note.time_adjustment) {
      return 'Suku Cadang & Waktu';
    } else if (note.parts_needed) {
      return 'Permintaan Suku Cadang';
    } else if (note.time_adjustment) {
      return 'Penyesuaian Waktu';
    } else {
      return 'Kendala Teknis';
    }
  };

  const getSeverityLevel = (note) => {
    if (note.parts_needed && note.time_adjustment) {
      return 'Tinggi';
    } else if (note.parts_needed || note.time_adjustment) {
      return 'Sedang';
    } else {
      return 'Rendah';
    }
  };

  return (
    <DashboardLayout userRole="mechanic">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold text-gray-800 flex items-center">
            <FiFileText className="mr-2" /> Catatan Lapangan
          </h1>
          <button
            onClick={() => {
              setEditingId(null);
              setFormData({
                bookingId: '',
                note: '',
                partsNeeded: '',
                timeAdjustment: '',
              });
              setShowForm(!showForm);
            }}
            className="btn btn-primary flex items-center"
          >
            <FiPlus className="mr-1" /> {showForm ? 'Batal' : 'Tambah Catatan'}
          </button>
        </div>

        {showForm && (
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h2 className="text-lg font-medium mb-4">
              {editingId ? 'Edit Catatan' : 'Tambah Catatan Baru'}
            </h2>
            <form onSubmit={handleSubmit}>
              {!editingId && (
                <div className="mb-4">
                  <label className="form-label">Pekerjaan</label>
                  <select
                    name="bookingId"
                    value={formData.bookingId}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  >
                    <option value="">Pilih Pekerjaan</option>
                    {jobs.map(job => (
                      <option key={job.id} value={job.id}>
                        {job.service_name} - {job.customer_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="mb-4">
                <label className="form-label">Catatan</label>
                <textarea
                  name="note"
                  value={formData.note}
                  onChange={handleInputChange}
                  className="form-input h-32"
                  placeholder="Masukkan detail catatan lapangan..."
                  required
                ></textarea>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="mb-4">
                  <label className="form-label">Permintaan Suku Cadang</label>
                  <textarea
                    name="partsNeeded"
                    value={formData.partsNeeded}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Masukkan suku cadang yang diperlukan (opsional)"
                  ></textarea>
                </div>
                
                <div className="mb-4">
                  <label className="form-label">Penyesuaian Waktu</label>
                  <textarea
                    name="timeAdjustment"
                    value={formData.timeAdjustment}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Masukkan penyesuaian waktu yang diperlukan (opsional)"
                  ></textarea>
                </div>
              </div>
              
              <div className="flex justify-end">
                <button type="submit" className="btn btn-primary">
                  {editingId ? 'Update Catatan' : 'Simpan Catatan'}
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : notes.length === 0 ? (
          <div className="bg-gray-50 p-8 rounded-lg text-center">
            <p className="text-gray-500">Belum ada catatan lapangan.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tanggal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pekerjaan
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Jenis Masalah
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Urgensi
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Catatan
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aksi
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {notes.map((note) => (
                  <tr key={note.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(note.date).toLocaleDateString('id-ID')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {note.service_name} - {note.customer_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {getIssueType(note)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getSeverityClass(note)}`}>
                        {getSeverityLevel(note)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {note.note}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(note)}
                        className="text-primary hover:text-primary-dark mr-3"
                      >
                        <FiEdit />
                      </button>
                      <button
                        onClick={() => handleDelete(note.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default FieldNotes; 
